import axiosInstance from '../../api/axiosConfig';

const FALLBACK_SCHOOL_NAME = 'Demo School of Excellence';

const normalizeSchoolName = (value) => {
  const trimmed = (value || '').toString().trim();
  return trimmed || FALLBACK_SCHOOL_NAME;
};

const parseContent = (rawContent) => {
  if (rawContent === null || rawContent === undefined) {
    return null;
  }

  if (typeof rawContent === 'string') {
    const normalized = rawContent.trim();
    if (!normalized) {
      return null;
    }
    try {
      return JSON.parse(normalized);
    } catch (_error) {
      // Treat non-JSON strings as usable content.
      return normalized;
    }
  }

  return rawContent;
};

const ROUTE_SLUG_TO_CANONICAL_PAGE_SLUG = Object.freeze({
  home: 'home_page',
  about: 'about_page',
  academics: 'academics_page',
  admissions: 'enrollment_options',
  facilities: 'facilities_page',
  contact: 'contact_us_page',
});

const CORE_ROUTE_SLUGS = Object.freeze(Object.keys(ROUTE_SLUG_TO_CANONICAL_PAGE_SLUG));
const DEFAULT_CUSTOM_PAGE_ROUTE_SLUGS = Object.freeze(['results', 'announcements', 'news', 'careers']);

const toCustomPageSlug = (slug) => `custom_${slug}`;

export const resolveWebsitePageSlug = (slug) => {
  const normalized = (slug || '').toString().trim().toLowerCase();
  if (!normalized) {
    return '';
  }
  return ROUTE_SLUG_TO_CANONICAL_PAGE_SLUG[normalized] || toCustomPageSlug(normalized);
};

export const isCoreWebsiteRouteSlug = (slug) => {
  const normalized = (slug || '').toString().trim().toLowerCase();
  return Boolean(ROUTE_SLUG_TO_CANONICAL_PAGE_SLUG[normalized]);
};

export const CORE_WEBSITE_ROUTE_SLUGS = CORE_ROUTE_SLUGS;

const hasUsableContent = (responseData) => {
  if (!responseData || typeof responseData !== 'object') {
    return false;
  }

  if (Object.prototype.hasOwnProperty.call(responseData, 'content')) {
    const parsed = parseContent(responseData.content);
    if (parsed === null || parsed === undefined) {
      return false;
    }
    if (typeof parsed === 'string') {
      return parsed.trim().length > 0;
    }
    if (Array.isArray(parsed)) {
      return parsed.length > 0;
    }
    if (typeof parsed === 'object') {
      return Object.keys(parsed).length > 0;
    }
    return true;
  }

  // Some APIs may return raw object payloads without a "content" wrapper.
  if (responseData.sidebar && typeof responseData.sidebar === 'object') {
    return true;
  }

  return Object.keys(responseData).length > 0;
};

const createHeaderContent = (schoolName) => ({
  header: {
    brand: {
      logoSrc:
        'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=520&q=80',
      logoAlt: `${schoolName} logo`,
      brandName: schoolName,
      slogan: 'Nurturing Every Learner',
      sloganFontSize: '0.72rem',
      logoWidth: 42,
      logoHeight: 42,
      logoMobileWidth: 34,
      logoMobileHeight: 34,
      brandDisplayOption: { value: 'both' },
    },
    mainNav: [
      { label: 'Home', path: '/', enabled: true },
      { label: 'About', path: '/about', enabled: true },
      { label: 'Academics', path: '/academics', enabled: true },
      { label: 'Admissions', path: '/admissions', enabled: true },
      { label: 'Admissions Overview', path: '/page/admissions-overview', enabled: true },
      { label: 'Facilities', path: '/facilities', enabled: true },
      { label: 'Mission & Vision', path: '/page/mission-and-vision', enabled: true },
      { label: 'Results', path: '/results', enabled: true },
      { label: 'Announcements', path: '/announcements', enabled: true },
      { label: 'News', path: '/page/news', enabled: true },
      { label: 'Careers', path: '/careers', enabled: true },
      { label: 'Contact', path: '/contact', enabled: true },
    ],
    adminButton: {
      label: 'Admin Login',
      path: '/login',
    },
    navFontSize: 13,
    showMainNav: true,
    notificationConfig: {
      enabled: true,
      notificationMessage: 'Admissions 2026-27 Open',
      notificationDescription: 'Campus tours and counsellor guidance slots are now live.',
      blinkDuration: 3,
      actionButton: {
        enabled: true,
        label: 'Start Admission',
        path: '/admissions',
      },
    },
  },
});

const createHomeContent = (schoolName) => ({
  cover_photos: {
    items: [
      {
        image:
          'https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=2200&q=80',
        title: 'Top Rated CBSE Schools in Bangalore',
        subtitle: `${schoolName}: Find a campus near you with admissions open for 2026-27.`,
        slogan: 'Future-ready learning with heart.',
        showText: true,
        buttonLink: '/admissions',
        imageFilter: 'brightness(57%)',
        contentAlign: 'left',
      },
      {
        image:
          'https://images.unsplash.com/photo-1529390079861-591de354faf5?auto=format&fit=crop&w=2200&q=80',
        title: 'Curious Minds. Bold Ideas.',
        subtitle: 'Interactive classrooms, design labs, and project-led learning in every grade.',
        slogan: 'Think deeper. Build smarter.',
        showText: true,
        buttonLink: '/facilities',
        imageFilter: 'brightness(60%)',
        contentAlign: 'center',
      },
      {
        image:
          'https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=2200&q=80',
        title: 'Admissions Open',
        subtitle: 'Easy admissions flow, scholarship guidance, and one-to-one counselor support.',
        slogan: 'Book your visit. Feel the campus vibe.',
        showText: true,
        buttonLink: '/admissions',
        imageFilter: 'brightness(58%)',
        contentAlign: 'left',
      },
    ],
  },
  key_people: {
    title: 'Meet the Vision Team',
    subtitle: 'Educators and innovators designing a joyful, high-performance school journey.',
    headingColor: '#0F172A',
    backgroundColor: '#F0F9FF',
    people: [
      {
        id: 'leader-1',
        name: 'Dr. Priya Rao',
        role: 'Principal',
        bio: 'Leads school transformation with student-first culture, mentoring systems, and outcome-driven planning.',
        photo:
          'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=900&q=80',
      },
      {
        id: 'leader-2',
        name: 'Mr. Arjun Mehta',
        role: 'Director of Academics',
        bio: 'Designs multi-layered curriculum pathways with hands-on learning and assessment clarity.',
        photo:
          'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=900&q=80',
      },
      {
        id: 'leader-3',
        name: 'Ms. Neha Sinha',
        role: 'Student Experience Head',
        bio: 'Shapes wellbeing programs, leadership clubs, arts culture, and parent engagement.',
        photo:
          'https://images.unsplash.com/photo-1544717301-9cdcb1f5940f?auto=format&fit=crop&w=900&q=80',
      },
    ],
  },
  specialized: {
    aboutUsSection: {
      enabled: true,
      title: `Welcome to ${schoolName}`,
      tagline: 'A vibrant, curvy, student-centered campus experience designed for modern learners.',
      overview:
        'We blend academics, creativity, technology, and strong values so students can explore, express, and excel with confidence.',
      keyBenefits: [
        'Smart classrooms and activity-led teaching',
        'Coding, robotics, debate, arts, and sports clubs',
        'Structured mentoring with monthly progress review',
        'Safe campus systems with transparent parent communication',
      ],
      callToAction: {
        text: 'Explore Our Programs',
        link: '/admissions',
      },
      backgroundColor: '#ECFEFF',
      image:
        'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1600&q=80',
      imageAltText: 'Students collaborating in class',
    },
    specializationsSection: {
      enabled: true,
      title: 'Signature Learning Tracks',
      overview: 'Choice-based enrichment tracks that help students discover strengths beyond textbooks.',
      highlightedSpecializations: ['STEM & Innovation', 'Public Speaking', 'Visual Arts', 'Athletics'],
    },
  },
  school_statistics: {
    title: 'At a Glance',
    items: [
      { icon: 'S', value: '100+', label: 'Schools', description: 'Top-rated campuses across India' },
      { icon: 'C', value: '20+', label: 'Cities', description: 'Strong national presence' },
      { icon: 'F', value: '7000+', label: 'Teachers', description: 'Qualified and trained educators' },
      { icon: 'P', value: '75000+', label: 'Students', description: 'Trusted by parent community' },
    ],
  },
  school_programs: {
    title: 'Academic Tracks for Every Learner',
    subtitle: 'Progressive pathways from foundational learning to board and career readiness.',
    items: [
      {
        id: 'program-foundation',
        title: 'Foundation Years',
        grades: 'Pre-K to Grade 2',
        description: 'Language, numeracy, curiosity, and confidence through joyful classroom experiences.',
        image:
          'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1600&q=80',
      },
      {
        id: 'program-exploration',
        title: 'Exploration Years',
        grades: 'Grades 3 to 7',
        description: 'STEM exposure, reading fluency, maker projects, and collaborative team learning.',
        image:
          'https://images.unsplash.com/photo-1456324504439-367cee3b3c32?auto=format&fit=crop&w=1600&q=80',
      },
      {
        id: 'program-advanced',
        title: 'Advanced Years',
        grades: 'Grades 8 to 12',
        description: 'Board preparation, labs, mentoring, electives, and entrance-readiness support.',
        image:
          'https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?auto=format&fit=crop&w=1600&q=80',
      },
    ],
  },
  student_life: {
    title: 'School Life Beyond Classrooms',
    subtitle: 'A vibrant routine of clubs, sports, arts, and leadership opportunities.',
    activities: [
      {
        id: 'student-life-1',
        title: 'STEM & Robotics Club',
        description: 'Weekly coding and robotics sessions with challenge-based learning.',
        image:
          'https://images.unsplash.com/photo-1581092335397-9583eb92d232?auto=format&fit=crop&w=1400&q=80',
      },
      {
        id: 'student-life-2',
        title: 'Sports Development',
        description: 'Structured coaching in athletics, football, basketball, and indoor fitness.',
        image:
          'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?auto=format&fit=crop&w=1400&q=80',
      },
      {
        id: 'student-life-3',
        title: 'Arts & Performance',
        description: 'Music, dance, theatre, and public stage opportunities every term.',
        image:
          'https://images.unsplash.com/photo-1511192336575-5a79af67a629?auto=format&fit=crop&w=1400&q=80',
      },
      {
        id: 'student-life-4',
        title: 'Leadership & Service',
        description: 'Student council, peer mentoring, and social responsibility drives.',
        image:
          'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1400&q=80',
      },
    ],
  },
  admission_pathway: {
    title: 'Simple Admission Journey',
    subtitle: 'Transparent and guided process for families applying to our demo school.',
    steps: [
      {
        step: 'Submit Inquiry',
        detail: 'Share grade details and contact information through the online form.',
      },
      {
        step: 'Counselor Connect',
        detail: 'Get one-on-one guidance for curriculum, campus life, and eligibility.',
      },
      {
        step: 'Campus Tour',
        detail: 'Visit classrooms, labs, sports zones, and meet teaching staff.',
      },
      {
        step: 'Seat Confirmation',
        detail: 'Complete documentation, fee process, and onboarding timeline.',
      },
    ],
  },
  campuses: {
    title: 'Choose from 39+ Top Schools in Bangalore',
    city: 'Bangalore',
    items: [
      'BTM Layout',
      'Bannerghatta',
      'CV Raman Nagar',
      'Haralur',
      'Horamavu',
      'JP Nagar',
      'Jalahalli',
      'Kadugodi',
      'Magadi Road',
      'Mahalakshmi Layout',
      'Majestic',
      'Mysore Road',
      'Nagarbhavi',
      'Panathur',
      'Rajaji Nagar',
      'Sahakar Nagar',
      'Sarjapur Road',
      'Vijaya Nagar',
      'Yelahanka',
      'BTM Layout (Pre-Primary Wing)',
      'Sahakar Nagar (Pre-Primary Wing)',
      'Vijayanagar (Pre Primary Wing)',
      'Whitefield',
      'Kanakapura Road',
      'Kanakapura Road (Residential)',
      'Hennur Road',
      'Jakkur',
      'Makali',
      'Kengeri',
      'Yelahanka II',
      'Cheemasandra',
      'Hesaraghatta Road',
      'Kumbalgodu',
      'Rayasandra',
      'Ramamurthy Nagar',
      'Mahadevapura',
      'Marathahalli',
      'HSR Layout',
      'Yelahanka Newtown',
    ],
  },
  board_results: {
    title: '363 Orchidians Scored Over 90% at CBSE Boards 2025',
    toppers: [
      'Atharva Gupta',
      'Mihika Agarwal',
      'Ayaan Sanjay Sonigara',
      'Aryan Marwah',
      'Ojas Dnyaneshwar Deshmukh',
      'Mansi Maheshwari',
    ],
  },
  comparison: {
    title: 'Why Parents Choose Orchids Over Other Schools',
    columns: ['Aspect', 'Orchids International School', 'Other Schools'],
    rows: [
      {
        aspect: 'Curriculum',
        orchids: 'CBSE + Innovation Model with structured milestones',
        others: 'Standard CBSE/ICSE only',
      },
      {
        aspect: 'Early Years Program',
        orchids: 'Literacy, numeracy, motor and sensory development',
        others: 'Basic preschool curriculum',
      },
      {
        aspect: 'Technology Integration',
        orchids: 'Robotics lab, digi-boards, and tab-based learning',
        others: 'Limited or no integration',
      },
      {
        aspect: 'Experiential Learning',
        orchids: 'Field trips, experiments, and hands-on activity kits',
        others: 'Mostly textbook-based learning',
      },
      {
        aspect: 'National Presence',
        orchids: '100+ schools in 20+ cities',
        others: 'Limited presence',
      },
      {
        aspect: 'Teacher Strength',
        orchids: '7000+ trained, qualified educators',
        others: 'Smaller, less trained faculty',
      },
      {
        aspect: 'Campus Safety',
        orchids: 'CCTV-enabled, secure, modern facilities',
        others: 'Basic infrastructure',
      },
      {
        aspect: 'Parent Communication',
        orchids: 'Real-time updates, PTMs, and parent workshops',
        others: 'Limited parental engagement',
      },
      {
        aspect: 'Co-curriculars',
        orchids: 'Dance, music, skating, art, and swimming as core learning',
        others: 'Often treated as optional',
      },
    ],
  },
  activities: {
    title: 'Our Activities',
    items: [
      'Theatre',
      'Textile',
      'Dance',
      'Arts',
      'Music',
      'Taekwondo',
      'Swimming',
      'Football',
      'Public Speaking',
      'Pottery',
      'Astronomy',
      'Horticulture',
      'Robotics',
      'Sports',
      'Library Programme',
      'Student Council',
      'Trip and Expeditions',
      'Health and Nutrition',
      'Infrastructure',
      'Teacher Training',
      'Coding',
      'Weaving',
      'Basketball',
      'Chess',
      'Cricket',
    ],
  },
  learning_journey: {
    title: 'A Nurturing Journey from Nursery to High School',
    subtitle:
      'Programs are designed for each growth stage, from playful early years to board readiness and career clarity.',
    stages: [
      {
        title: 'Nursery / Early Years Program (EYP)',
        description:
          'Playful exploration, sensory discovery, literacy and numeracy readiness, motor skills, storytelling, and safe social interaction.',
      },
      {
        title: 'Primary School (Class 1-5)',
        description:
          'Strong language, maths, and EVS foundation with science kits, Olympiad preparation, assessments, arts, and public speaking.',
      },
      {
        title: 'Middle School (Class 6-8)',
        description:
          'Interdisciplinary learning, project-based assessments, coding, robotics, financial literacy, clubs, and leadership opportunities.',
      },
      {
        title: 'High School (Class 9-12)',
        description:
          'Advanced academics, board exam readiness, stream-based support, mock tests, mentoring, and career counselling.',
      },
    ],
  },
  fee_structure: {
    title: 'Transparent Fee Structure Across Bangalore Campuses',
    rows: [
      { slNo: 1, branchName: 'ORCHIDS BTM Layout', monthlyFees: '12,083/- PM+' },
      { slNo: 2, branchName: 'ORCHIDS Bannerghatta', monthlyFees: '8,667/- PM+' },
      { slNo: 3, branchName: 'ORCHIDS CV Raman Nagar', monthlyFees: '11,083/- PM+' },
      { slNo: 4, branchName: 'ORCHIDS Haralur', monthlyFees: '9,167/- PM+' },
      { slNo: 5, branchName: 'ORCHIDS Horamavu', monthlyFees: '8,750/- PM+' },
      { slNo: 6, branchName: 'ORCHIDS JP Nagar', monthlyFees: '9,000/- PM+' },
      { slNo: 7, branchName: 'ORCHIDS Jalahalli', monthlyFees: '8,417/- PM+' },
      { slNo: 8, branchName: 'ORCHIDS Kadugodi', monthlyFees: '9,000/- PM+' },
      { slNo: 9, branchName: 'ORCHIDS Magadi Road', monthlyFees: '7,250/- PM+' },
      { slNo: 10, branchName: 'ORCHIDS Mahalakshmi Layout', monthlyFees: '8,583/- PM+' },
      { slNo: 11, branchName: 'ORCHIDS Majestic', monthlyFees: '7,750/- PM+' },
      { slNo: 12, branchName: 'ORCHIDS Mysore Road', monthlyFees: '9,000/- PM+' },
      { slNo: 13, branchName: 'ORCHIDS Nagarbhavi', monthlyFees: '9,167/- PM+' },
      { slNo: 14, branchName: 'ORCHIDS Panathur', monthlyFees: '10,000/- PM+' },
      { slNo: 15, branchName: 'ORCHIDS Rajaji Nagar', monthlyFees: '7,667/- PM+' },
      { slNo: 16, branchName: 'ORCHIDS Sahakar Nagar', monthlyFees: '9,583/- PM+' },
      { slNo: 17, branchName: 'ORCHIDS Sarjapur Road', monthlyFees: '9,583/- PM+' },
      { slNo: 18, branchName: 'ORCHIDS Vijaya Nagar', monthlyFees: '11,000/- PM+' },
      { slNo: 19, branchName: 'ORCHIDS Yelahanka', monthlyFees: '8,250/- PM+' },
      { slNo: 20, branchName: 'BTM Layout (Pre-Primary Wing)', monthlyFees: '7,500/- PM+' },
      { slNo: 21, branchName: 'Sahakar Nagar (Pre-Primary Wing)', monthlyFees: '8,166/- PM+' },
      { slNo: 22, branchName: 'Vijayanagar (Pre Primary Wing)', monthlyFees: '7,917/- PM+' },
      { slNo: 23, branchName: 'ORCHIDS Whitefield', monthlyFees: '7,916/- PM+' },
      { slNo: 24, branchName: 'ORCHIDS Kanakapura Road', monthlyFees: '5,667/- PM+' },
      { slNo: 25, branchName: 'ORCHIDS Kanakapura Road (Residential)', monthlyFees: '20,100/- PM+' },
      { slNo: 26, branchName: 'ORCHIDS Hennur Road', monthlyFees: '7,917/- PM+' },
      { slNo: 27, branchName: 'ORCHIDS Jakkur', monthlyFees: '7,916/- PM+' },
      { slNo: 28, branchName: 'ORCHIDS Makali', monthlyFees: '6,250/- PM+' },
      { slNo: 29, branchName: 'ORCHIDS Kengeri', monthlyFees: '7,500/- PM+' },
      { slNo: 30, branchName: 'ORCHIDS Yelahanka II', monthlyFees: '6,458/- PM+' },
      { slNo: 31, branchName: 'ORCHIDS Cheemasandra', monthlyFees: '6,250/- PM+' },
      { slNo: 32, branchName: 'ORCHIDS Hesaraghatta Road', monthlyFees: '5,450/- PM+' },
      { slNo: 33, branchName: 'ORCHIDS Kumbalgodu', monthlyFees: '6,600/- PM+' },
      { slNo: 34, branchName: 'ORCHIDS Rayasandra', monthlyFees: '6,250/- PM+' },
      { slNo: 35, branchName: 'ORCHIDS Ramamurthy Nagar', monthlyFees: '6,667/- PM+' },
      { slNo: 36, branchName: 'ORCHIDS Mahadevapura', monthlyFees: '7,917/- PM+' },
      { slNo: 37, branchName: 'ORCHIDS Marathahalli', monthlyFees: '7,292/- PM+' },
      { slNo: 38, branchName: 'ORCHIDS HSR Layout', monthlyFees: '7,500/- PM+' },
      { slNo: 39, branchName: 'ORCHIDS Yelahanka Newtown', monthlyFees: '7,500/- PM+' },
    ],
  },
  petals_of_purpose: {
    title: "Orchids's 5 Petals of Purpose",
    items: [
      {
        title: 'A Fortress of Care',
        description: "There's zero compromise when it comes to your child's safety.",
      },
      {
        title: 'Cultivating the X Factor',
        description: "Excellence along with the X factor needed to thrive in tomorrow's world.",
      },
      {
        title: 'State-of-the-art Infrastructure',
        description: 'Because your child deserves the best.',
      },
      {
        title: 'Never A Dull Day',
        description: "Whatever your interests are, you'll find your niche at school.",
      },
      {
        title: 'From ABC to AI',
        description: 'Solid academic foundation with ready access to the latest technology.',
      },
    ],
  },
  philosophy: {
    title: 'Our Philosophy',
    points: [
      {
        title: 'Self-Discipline',
        description:
          'Self-discipline makes students independent of monitored discipline and strengthens belief in education.',
      },
      {
        title: 'Hard Work',
        description:
          "Hard work sharpens natural qualities. We enhance children's capabilities through consistent development.",
      },
      {
        title: 'Applied Science',
        description:
          'Applied science removes fear of complex topics through experiential learning and clear concepts.',
      },
      {
        title: 'Research',
        description:
          'Research builds the habit of learning in depth through reading, inquiry, and exploration.',
      },
      {
        title: 'Physical Fitness',
        description:
          'Physical fitness develops a healthy body and mind through nutrition and sports programs.',
      },
      {
        title: 'Exposure',
        description:
          'Field trips and experiential programs prepare children for real-life situations.',
      },
      {
        title: 'Reflective Thinking',
        description:
          'Reflective and independent thinking builds critical and analytical decision-making abilities.',
      },
    ],
  },
  faqs: {
    title: 'Frequently Asked Questions',
    items: [
      {
        question: 'Which is the best CBSE school in Bangalore for holistic development?',
        answer:
          'Families usually look for strong academics, student wellbeing, and activity exposure in one campus network.',
      },
      {
        question: 'How many branches are there in Bangalore?',
        answer: 'This demo profile includes 39 Bangalore campuses.',
      },
      {
        question: 'What is the fee structure for Bangalore branches?',
        answer:
          'Monthly fees vary by branch and facilities. The detailed fee table is provided in the fee structure section.',
      },
      {
        question: 'Do all Bangalore branches follow CBSE curriculum?',
        answer: 'Yes, this profile is configured around CBSE-aligned academics with innovation components.',
      },
      {
        question: 'Do campuses have robotics and coding labs?',
        answer: 'Yes, robotics and coding are part of the learning ecosystem in this demo profile.',
      },
      {
        question: 'What extracurricular activities are available?',
        answer: 'Sports, arts, music, theatre, speaking programs, and clubs are included.',
      },
      {
        question: 'Are transportation facilities available?',
        answer: 'Transport support is available based on branch routes and coverage.',
      },
      {
        question: 'How can parents apply for admission?',
        answer: 'Parents can submit enquiry, connect with a counsellor, take a campus tour, and complete seat confirmation.',
      },
      {
        question: 'What grades are offered?',
        answer: 'Programs are available from nursery/early years to high school.',
      },
      {
        question: 'Are safety and CCTV monitoring available on campus?',
        answer: 'Yes, safety processes and campus monitoring are core priorities in this profile.',
      },
    ],
  },
  testimonials: {
    title: 'Orchids the International School in Bangalore Reviews by Parents',
    backgroundImage:
      'https://images.unsplash.com/photo-1544717305-996b815c338c?auto=format&fit=crop&w=2000&q=80',
    list: [
      {
        id: 'testimonial-1',
        name: 'Aditya S.',
        title: 'Grade 8 Student',
        message:
          'Teachers who truly care. I love how our teachers make every concept clear and give exposure through competitions and workshops.',
        avatar:
          'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=400&q=80',
      },
      {
        id: 'testimonial-2',
        name: 'Kiran Kumar',
        title: 'Parent',
        message:
          'Modern facilities and great communication. Campus infrastructure and labs are impressive, and parent updates are transparent.',
        avatar:
          'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=400&q=80',
      },
      {
        id: 'testimonial-3',
        name: 'Meena Raj',
        title: 'Parent',
        message:
          'Encourages overall personality development. From academics to stage activities, my daughter has grown more confident.',
        avatar:
          'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=crop&w=400&q=80',
      },
      {
        id: 'testimonial-4',
        name: 'Rashmi Rao',
        title: 'Parent',
        message:
          "Excellent academic standards. We've seen remarkable improvement in academic performance and confidence.",
        avatar:
          'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80',
      },
      {
        id: 'testimonial-5',
        name: 'Vikram Shetty',
        title: 'Parent',
        message:
          'Safe and nurturing environment. Safety protocols and personal attention are strong and dependable.',
        avatar:
          'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=400&q=80',
      },
      {
        id: 'testimonial-6',
        name: 'Anjali Nair',
        title: 'Parent',
        message:
          'Holistic learning at its best. Academics and co-curricular programs like robotics, dance, and art are handled seriously.',
        avatar:
          'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=400&q=80',
      },
    ],
  },
  mobile_app_details: {
    title: 'Smart Parent & Student App',
    description: 'Attendance, homework, notices, transport, and fee updates with real-time alerts in one app.',
    image:
      'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1600&q=80',
    features: [
      {
        iconName: 'ScheduleIcon',
        title: 'Live Timetable',
        description: 'Daily schedules, substitutions, and activity updates in real time.',
      },
      {
        iconName: 'ChatBubbleIcon',
        title: 'Parent Messages',
        description: 'Direct updates from teachers, class mentors, and school office.',
      },
      {
        iconName: 'DirectionsBusIcon',
        title: 'Transport Tracking',
        description: 'Live bus location, route ETA, and pickup alerts for families.',
      },
      {
        iconName: 'AssessmentIcon',
        title: 'Progress Reports',
        description: 'Attendance, marks, remarks, and report cards on demand.',
      },
      {
        iconName: 'EventNoteIcon',
        title: 'Events & Circulars',
        description: 'Never miss competitions, exams, holidays, and important notices.',
      },
    ],
    callToAction: {
      android: {
        link: 'https://play.google.com/store',
        buttonText: 'Get on Android',
      },
      ios: {
        link: 'https://www.apple.com/app-store/',
        buttonText: 'Get on iOS',
      },
    },
    animation: {
      enabled: true,
      lottieAnimation: 'https://assets3.lottiefiles.com/packages/lf20_0yfsb3a1.json',
      lottieSize: 56,
    },
  },
  events: {
    title: 'Campus Highlights',
    subtitle: 'Colorful spaces designed for curiosity, collaboration, creativity, and competitive spirit.',
    headingColor: '#0F172A',
    backgroundColor: '#F8FAFC',
    amenities: [
      {
        id: 'amenity-1',
        name: 'Discovery Science Lab',
        image:
          'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=1200&q=80',
      },
      {
        id: 'amenity-2',
        name: 'Library',
        image:
          'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?auto=format&fit=crop&w=1200&q=80',
      },
      {
        id: 'amenity-3',
        name: 'Sports Arena',
        image:
          'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?auto=format&fit=crop&w=1200&q=80',
      },
      {
        id: 'amenity-4',
        name: 'Innovation Studio',
        image:
          'https://images.unsplash.com/photo-1516321497487-e288fb19713f?auto=format&fit=crop&w=1200&q=80',
      },
      {
        id: 'amenity-5',
        name: 'Performing Arts Hall',
        image:
          'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=1200&q=80',
      },
    ],
  },
  join_comunity: {
    title: `Join ${schoolName}`,
    subtitle: 'Start your child\'s journey with counseling support, scholarships guidance, and a smooth onboarding process.',
    image:
      'https://images.unsplash.com/photo-1516534775068-ba3e7458af70?auto=format&fit=crop&w=2000&q=80',
    overlayColor: 'rgba(9, 19, 52, 0.64)',
    titleColor: '#FFFFFF',
    subtitleColor: '#E0ECFF',
    button: {
      text: 'Apply Now',
      link: '/admissions',
    },
    buttonColor: '#059669',
    buttonHoverColor: '#0284C7',
    buttonTextColor: '#FFFFFF',
  },
});

const createSidebarContent = (schoolName) => ({
  school: {
    name: schoolName,
    website: '',
  },
  announcements: [
    {
      id: 'announcement-default-1',
      title: 'Admissions Window Live',
      content: 'Admissions for the 2026-27 batch are now open with campus counseling support.',
      createdAt: new Date().toISOString(),
    },
    {
      id: 'announcement-default-2',
      title: 'Scholarship Aptitude Round',
      content: 'Scholarship screening for Grades 6-10 is scheduled this month. Limited seats.',
      createdAt: new Date().toISOString(),
    },
  ],
  news: [
    {
      id: 'news-default-1',
      title: 'Innovation Week 2026',
      content: 'Students will showcase robotics, AI mini-projects, and design prototypes this Friday.',
      createdAt: new Date().toISOString(),
    },
    {
      id: 'news-default-2',
      title: 'Sports Excellence Program',
      content: 'New evening coaching batches launched for basketball, athletics, and football.',
      createdAt: new Date().toISOString(),
    },
  ],
  holidays: [
    {
      id: 'holiday-default-1',
      title: 'Founders Day',
      date: '2026-08-15',
      createdAt: new Date().toISOString(),
    },
    {
      id: 'holiday-default-2',
      title: 'Winter Break',
      date: '2026-12-24',
      createdAt: new Date().toISOString(),
    },
  ],
  gallery: [
    {
      id: 'gallery-default-1',
      title: 'Learning Atrium',
      description: 'Students collaborating in our colorful open learning atrium.',
      fileUrl:
        'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1200&q=80',
      uploadedAt: new Date().toISOString(),
    },
    {
      id: 'gallery-default-2',
      title: 'STEM Lab',
      description: 'Hands-on innovation sessions in our STEM and robotics lab.',
      fileUrl:
        'https://images.unsplash.com/photo-1581092335397-9583eb92d232?auto=format&fit=crop&w=1200&q=80',
      uploadedAt: new Date().toISOString(),
    },
    {
      id: 'gallery-default-3',
      title: 'Performing Arts',
      description: 'Music and stage rehearsal sessions in the performing arts studio.',
      fileUrl:
        'https://images.unsplash.com/photo-1511192336575-5a79af67a629?auto=format&fit=crop&w=1200&q=80',
      uploadedAt: new Date().toISOString(),
    },
    {
      id: 'gallery-default-4',
      title: 'Sports Arena',
      description: 'Students training in athletics and team sports programs.',
      fileUrl:
        'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?auto=format&fit=crop&w=1200&q=80',
      uploadedAt: new Date().toISOString(),
    },
  ],
});

const createFooterContent = (schoolName) => ({
  backgroundColor: '#11244D',
  textColor: '#E2ECFF',
  headerColor: '#A5F3FC',
  schoolInfo: {
    name: schoolName,
    addresses: [
      {
        addressLine1: '123 Learning Avenue, Knowledge District',
        addressLine2: 'Your City, State 12345',
      },
    ],
  },
  schoolTimings: 'Mon - Sat: 8:00 AM - 4:30 PM',
  aboutSchool:
    `${schoolName} delivers a modern school journey with strong academics, joyful co-curriculars, and future-ready student development.`,
  contact: {
    phone1: '+1 (555) 245-7788',
    email1: 'hello@yourschool.edu',
  },
  social: [
    { icon: 'Facebook', url: 'https://facebook.com' },
    { icon: 'Instagram', url: 'https://instagram.com' },
    { icon: 'YouTube', url: 'https://youtube.com' },
    { icon: 'LinkedIn', url: 'https://linkedin.com' },
  ],
  quickLinks: [
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about' },
    { label: 'Facilities', path: '/facilities' },
    { label: 'Academics', path: '/academics' },
    { label: 'Admissions', path: '/admissions' },
    { label: 'Contact', path: '/contact' },
  ],
  websiteInfoLinks: [
    { label: 'Privacy Policy', path: '/privacy-policy' },
    { label: 'Terms of Service', path: '/terms-of-service' },
  ],
  newsletter: {
    prompt: 'School Newsletter',
    description: 'Get admissions alerts, event highlights, and parent updates every week.',
  },
  copyright: `(c) ${new Date().getFullYear()} ${schoolName}. All rights reserved.`,
});

const createAboutContent = (schoolName) => ({
  content: {
    sections: [
      {
        id: 'about-overview',
        title: `About ${schoolName}`,
        type: 'overview',
        disabled: false,
        content:
          `${schoolName} is a vibrant learning community where academics, creativity, life skills, and values come together to shape confident learners.`,
        image:
          'https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=1500&q=80',
      },
      {
        id: 'about-mission-vision',
        title: 'Mission and Vision',
        type: 'text',
        disabled: false,
        content:
          'Our mission is to deliver high-impact learning through strong teaching, mentoring, and innovation. Our vision is to nurture responsible, curious, and future-ready learners.',
      },
      {
        id: 'about-pillars',
        title: 'What Makes Us Different',
        type: 'grid',
        disabled: false,
        items: [
          {
            name: 'Academic Mastery',
            logo:
              'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=220&q=80',
          },
          {
            name: 'Student Wellbeing',
            logo:
              'https://images.unsplash.com/photo-1529390079861-591de354faf5?auto=format&fit=crop&w=220&q=80',
          },
          {
            name: 'Future Skills & Innovation',
            logo:
              'https://images.unsplash.com/photo-1516321497487-e288fb19713f?auto=format&fit=crop&w=220&q=80',
          },
          {
            name: 'Leadership & Expression',
            logo:
              'https://images.unsplash.com/photo-1494172961521-33799ddd43a5?auto=format&fit=crop&w=220&q=80',
          },
        ],
      },
      {
        id: 'about-team',
        title: 'Leadership Team',
        type: 'team',
        disabled: false,
        members: [
          {
            name: 'Dr. Priya Rao',
            designation: 'Principal',
            bio: 'Leads school excellence and student-first initiatives.',
            photo:
              'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=800&q=80',
          },
          {
            name: 'Mr. Arjun Mehta',
            designation: 'Academic Director',
            bio: 'Guides curriculum planning and faculty development.',
            photo:
              'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=800&q=80',
          },
          {
            name: 'Ms. Neha Sinha',
            designation: 'Student Development Head',
            bio: 'Leads student wellbeing and parent engagement programs.',
            photo:
              'https://images.unsplash.com/photo-1544717301-9cdcb1f5940f?auto=format&fit=crop&w=800&q=80',
          },
        ],
      },
      {
        id: 'about-campus',
        title: 'Campus and Location',
        type: 'addressVideo',
        disabled: false,
        content:
          'Our campus includes immersive classrooms, innovation labs, arts studios, athletic zones, and secure student support spaces.',
        mapEmbedUrl: 'https://maps.google.com/maps?q=New%20York%20City&t=&z=13&ie=UTF8&iwloc=&output=embed',
        videoUrl: 'https://www.youtube.com/embed/Zi_XLOBDo_Y',
      },
      {
        id: 'about-principal-message',
        title: 'Message from the Principal',
        type: 'director',
        disabled: false,
        content:
          'We believe every child learns best when challenged, supported, and celebrated. Our school culture is built on curiosity, compassion, and consistent excellence.',
        image:
          'https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1200&q=80',
      },
      {
        id: 'about-cta',
        title: 'Visit and Connect',
        type: 'text',
        disabled: false,
        content:
          'Visit our campus, meet our educators, and experience how we transform everyday learning into a joyful growth journey.',
      },
    ],
  },
});

const createContactContent = (schoolName) => ({
  heroSlides: [
    {
      title: `Contact ${schoolName}`,
      subtitle: 'Connect with our team for admissions, tours, scholarship support, and program details.',
      image:
        'https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1800&q=80',
      buttonText: 'Book a Visit',
      buttonLink: '/admissions',
    },
    {
      title: 'Admissions Support',
      subtitle: 'Our counselors help with eligibility, documents, timelines, and grade placement.',
      image:
        'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1800&q=80',
      buttonText: 'Start Application',
      buttonLink: '/admissions',
    },
  ],
  missionVision: {
    missionHeading: 'Our Mission',
    missionDescription:
      'To provide a joyful, safe, and challenging learning environment where every child can do their best work.',
    visionHeading: 'Our Vision',
    visionDescription:
      'To be a trusted school recognized for academic excellence, creativity, empathy, and social impact.',
    imageUrl:
      'https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=1600&q=80',
  },
  keyValues: [
    { title: 'Student First', description: 'Every decision starts with student growth, safety, and wellbeing.' },
    { title: 'Innovation', description: 'Digital tools and hands-on tasks make learning deeper and relevant.' },
    { title: 'Community', description: 'Parent-school partnership strengthens outcomes and confidence.' },
    { title: 'Integrity', description: 'We practice transparency, responsibility, and consistency in every process.' },
  ],
  contactDetails: {
    addressHeading: 'Campus Address',
    address: '123 Learning Avenue, Knowledge District, Your City, State 12345',
    phoneHeading: 'Phone',
    phone: '+1 (555) 245-7788',
    emailHeading: 'Email',
    email: 'admissions@yourschool.edu',
    mapEmbedUrl: 'https://maps.google.com/maps?q=New%20York%20City&t=&z=13&ie=UTF8&iwloc=&output=embed',
  },
  styles: {
    primaryColor: '#0F172A',
    secondaryColor: '#DC2626',
    backgroundColor: '#ECFEFF',
    textColor: '#0B1220',
    accentColor: '#059669',
  },
});

const createFacilitiesContent = () => ({
  headerTitle: 'Our Facilities',
  bannerImage:
    'https://images.unsplash.com/photo-1588072432836-e10032774350?auto=format&fit=crop&w=1800&q=80',
  backgroundColor: '#F0F9FF',
  headerColor: '#0F172A',
  title: 'Discover Our Colorful Learning Spaces',
  subtitle: 'Purpose-built environments for academics, arts, athletics, and future skills.',
  subtitleColor: '#334155',
  titleColor: '#0B1220',
  facilities: [
    {
      id: 'facility-1',
      title: 'STEM Discovery Lab',
      description: 'Hands-on science, engineering, and maker activities for concept-to-prototype learning.',
      image:
        'https://images.unsplash.com/photo-1581090464777-f3220bbe1b8b?auto=format&fit=crop&w=1400&q=80',
      visible: true,
      keyHighlights: ['Modern lab setup', 'Safe supervised practicals', 'Project and prototype learning'],
    },
    {
      id: 'facility-2',
      title: 'Learning Commons Library',
      description: 'Curated books, reading nooks, digital resources, and guided reading support.',
      image:
        'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?auto=format&fit=crop&w=1400&q=80',
      visible: true,
      keyHighlights: ['Reading zones', 'Digital catalog', 'Reference and research support'],
    },
    {
      id: 'facility-3',
      title: 'Sports Arena',
      description: 'Indoor and outdoor sports infrastructure with coaching and fitness support.',
      image:
        'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?auto=format&fit=crop&w=1400&q=80',
      visible: true,
      keyHighlights: ['Indoor and outdoor courts', 'Fitness training support', 'Inter-school tournaments'],
    },
    {
      id: 'facility-4',
      title: 'Innovation Studio',
      description: 'Maker space for robotics, coding, design thinking, and collaborative challenges.',
      image:
        'https://images.unsplash.com/photo-1516321497487-e288fb19713f?auto=format&fit=crop&w=1400&q=80',
      visible: true,
      keyHighlights: ['Robotics kits', 'Coding challenges', 'Prototype workstations'],
    },
    {
      id: 'facility-5',
      title: 'Performing Arts Studio',
      description: 'Creative space for music, theatre, dance, and stage confidence.',
      image:
        'https://images.unsplash.com/photo-1511192336575-5a79af67a629?auto=format&fit=crop&w=1400&q=80',
      visible: true,
      keyHighlights: ['Acoustic practice rooms', 'Stage rehearsal zone', 'Annual showcase events'],
    },
  ],
  additionalInfo: {
    virtualTour: {
      visible: true,
      description: 'Take a virtual walkthrough of learning blocks, labs, arts studio, and sports arena.',
      tourUrl: 'https://www.google.com/maps',
    },
    campusMap: {
      visible: true,
      description: 'View classroom blocks, admin office, sports fields, and pickup points.',
      mapUrl: 'https://www.google.com/maps',
    },
    testimonials: {
      visible: true,
      items: [
        {
          id: 'fac-testimonial-1',
          name: 'Riya Sharma',
          role: 'Grade 8 Student',
          comment: 'The STEM lab and innovation studio make learning practical and exciting every week.',
          visible: true,
        },
        {
          id: 'fac-testimonial-2',
          name: 'Aarav Singh',
          role: 'Parent',
          comment: 'Campus facilities are modern, safe, and very well maintained for student growth.',
          visible: true,
        },
      ],
    },
    faq: {
      visible: true,
      items: [
        {
          id: 'fac-faq-1',
          question: 'Are facilities available after school hours?',
          answer: 'Yes. Selected facilities like library, sports, and activity studios remain open as per schedule.',
          visible: true,
        },
        {
          id: 'fac-faq-2',
          question: 'Do students get supervised access to labs?',
          answer: 'Yes. Labs are used under faculty supervision with strict safety and usage protocols.',
          visible: true,
        },
      ],
    },
  },
});

const createAcademicsContent = () => ({
  hero: {
    enabled: true,
    title: 'An Academic Journey Built for Confident, Future-Ready Learners',
    subtitle: 'Strong curriculum, mentor-led classrooms, practical exploration, and measurable student growth from foundation years to board readiness.',
    highlights: ['Concept-based learning', 'Small-group mentoring', 'Digital classroom support', 'Assessment with feedback'],
    stats: [
      { value: '3', label: 'learning stages' },
      { value: '18+', label: 'mentor-led programs' },
      { value: '100%', label: 'progress review cycle' },
    ],
    coverImage:
      'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1800&q=80',
    callToActionText: 'Explore Admissions',
    callToActionLink: '/admissions',
  },
  overview: {
    enabled: true,
    title: 'A Clear Academic System with Personal Attention',
    description:
      'Our academic model blends strong subject foundations, project-based exposure, class-wise progression, and regular parent communication so every student grows with clarity and confidence.',
  },
  whyChoose: {
    enabled: true,
    title: 'Why Choose Our Academics',
    whyChoose: [
      { title: 'Structured Curriculum', description: 'Clear grade-wise progression with term goals and visible learning milestones.' },
      { title: 'Qualified Faculty', description: 'Experienced educators with subject depth, mentoring focus, and outcome ownership.' },
      { title: 'Continuous Feedback', description: 'Regular reviews, parent updates, remedial planning, and student-specific support.' },
      { title: 'Balanced Student Growth', description: 'Strong academics with clubs, leadership, speaking, labs, arts, and sports.' },
    ],
  },
  steps: {
    enabled: true,
    title: 'Academic Path',
    steps: [
      {
        title: 'Foundation Stage',
        description: 'Strong basics in literacy, numeracy, communication, curiosity, and classroom confidence through engaging early learning experiences.',
      },
      {
        title: 'Concept Stage',
        description: 'Hands-on projects, labs, interdisciplinary thinking, and stronger analytical skills across middle grades.',
      },
      {
        title: 'Advanced Stage',
        description: 'Board readiness, exam strategy, subject strengthening, and future pathway guidance in senior years.',
      },
    ],
  },
  curriculum: {
    enabled: true,
    title: 'Curriculum Overview',
    description: 'A balanced structure across academics, projects, expression, problem solving, and co-curricular engagement.',
    curriculumItems: [
      {
        title: 'Primary School',
        gradeRange: 'Grades 1-5',
        description: 'Foundational literacy, numeracy, reading culture, communication, and exploration-based learning.',
      },
      {
        title: 'Middle School',
        gradeRange: 'Grades 6-8',
        description: 'Concept building through labs, projects, interdisciplinary tasks, and collaborative learning.',
      },
      {
        title: 'Senior School',
        gradeRange: 'Grades 9-12',
        description: 'Advanced coursework, exam planning, application support, and career-focused guidance.',
      },
    ],
  },
  program: {
    enabled: true,
    title: 'Beyond the Classroom',
    description: 'Programs that strengthen confidence, leadership, expression, and future skills beyond routine academics.',
    programList: ['Coding & AI Club', 'Reading Lab', 'Debate Society', 'STEM Projects', 'Music & Arts', 'Sports Training'],
  },
  faculty: {
    enabled: true,
    title: 'Our Faculty',
    description: 'Dedicated teachers who teach with clarity, track progress, and mentor students consistently.',
    facultyMembers: [
      {
        name: 'Ms. Kavya Sharma',
        role: 'Mathematics',
        bio: 'Focuses on conceptual clarity, practice discipline, and real-world problem solving.',
        image:
          'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=crop&w=900&q=80',
      },
      {
        name: 'Mr. Rahul Iyer',
        role: 'Science',
        bio: 'Leads practical, inquiry-based science learning with strong lab orientation.',
        image:
          'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=900&q=80',
      },
      {
        name: 'Ms. Nisha Verma',
        role: 'English & Communication',
        bio: 'Builds reading confidence, public speaking, and structured writing across grades.',
        image:
          'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=900&q=80',
      },
    ],
  },
  achievements: {
    enabled: true,
    title: 'Academic Achievements',
    description: 'Consistent outcomes across academics, olympiads, competitions, and student development milestones.',
    achievementList: [
      {
        title: 'Board Examination Results',
        description: '98% pass rate with distinctions in Mathematics, Science, and English.',
        year: '2025',
      },
      {
        title: 'National Olympiad Recognition',
        description: 'Students ranked at district and state levels in Science and Mathematics olympiads.',
        year: '2025',
      },
      {
        title: 'Inter-School Debate & Quiz Wins',
        description: 'Winning teams in regional debate, quiz, and communication events.',
        year: '2024',
      },
    ],
  },
  keyDates: {
    enabled: true,
    title: 'Important Dates',
    keyDates: [
      { event: 'New Session Begins', date: 'June 10' },
      { event: 'Baseline Assessments', date: 'June 24 - June 28' },
      { event: 'Mid-Term Assessments', date: 'September 05 - September 15' },
      { event: 'Annual Examinations', date: 'February 10 - February 28' },
    ],
  },
  faqs: {
    enabled: true,
    title: 'FAQs',
    faqs: [
      {
        question: 'How is student progress tracked?',
        answer: 'Through class assessments, notebook review, projects, teacher observations, and scheduled parent review meetings.',
      },
      {
        question: 'Are remedial sessions available?',
        answer: 'Yes. Focused support and guided revision sessions are planned for students needing extra help.',
      },
      {
        question: 'Do students get exposure beyond textbook learning?',
        answer: 'Yes. Labs, projects, clubs, presentations, competitions, and digital learning activities are part of the academic journey.',
      },
    ],
  },
  testimonials: {
    enabled: true,
    title: 'Academic Testimonials',
    description: 'Feedback from students and parents on classroom learning and support.',
    testimonialList: [
      {
        quote: 'Teachers explain concepts clearly, track our progress, and help us prepare with confidence before every assessment.',
        author: 'Rohan V.',
        role: 'Grade 10 Student',
      },
      {
        quote: 'We have seen strong improvement in both academics and confidence because the school communicates clearly and supports students consistently.',
        author: 'Anita K.',
        role: 'Parent',
      },
    ],
  },
  callToAction: {
    enabled: true,
    title: 'Ready to Begin?',
    subtitle: 'Apply now and schedule a campus visit.',
    buttonText: 'Start Admission',
    buttonLink: '/admissions',
    additionalLinkText: 'Contact Us',
    additionalLink: '/contact',
  },
});

const createEnrollmentContent = () => ({
  overview: {
    enabled: true,
    title: 'Admissions',
    description: 'Submit your inquiry and our admissions team will guide you from counseling to final onboarding.',
  },
  whyChoose: {
    enabled: true,
    title: 'Why Join Us',
    whyChoose: [
      { title: 'Future-Ready Curriculum', description: 'Strong academics with coding, projects, and communication skills.' },
      { title: 'Safe & Inclusive Campus', description: 'Student-friendly environment with supervision and wellbeing systems.' },
      { title: 'Mentor-Led Growth', description: 'Experienced faculty with individual attention and progress tracking.' },
      { title: 'Strong Parent Connect', description: 'Transparent communication through meetings and real-time app updates.' },
    ],
  },
  admissionsProcess: {
    enabled: true,
    title: 'Admission Steps',
    admissionsProcess: [
      { step: 'Submit Inquiry', description: 'Complete and submit the online admissions inquiry form.' },
      { step: 'Counseling Session', description: 'Discuss grade placement, goals, and school fit with our counselor.' },
      { step: 'Campus Visit', description: 'Tour classrooms, labs, sports facilities, and meet faculty members.' },
      { step: 'Document Review', description: 'Share required documents for eligibility and verification.' },
      { step: 'Seat Confirmation', description: 'Complete admission formalities and begin onboarding.' },
    ],
  },
  enrollmentForm: {
    enabled: true,
  },
  enrollmentOptions: {
    gradeOptions: [
      'Pre-K',
      'LKG',
      'UKG',
      'Grade 1',
      'Grade 2',
      'Grade 3',
      'Grade 4',
      'Grade 5',
      'Grade 6',
      'Grade 7',
      'Grade 8',
      'Grade 9',
      'Grade 10',
      'Grade 11',
      'Grade 12',
    ],
    areasOptions: ['Science', 'Mathematics', 'Arts', 'Sports', 'Music', 'Technology', 'Public Speaking', 'Robotics'],
    languageOptions: ['English', 'Hindi', 'Spanish', 'French', 'German'],
  },
  callToAction: {
    enabled: true,
    contactInfo: 'Need help? Contact admissions at +1 (555) 245-7788 or email admissions@yourschool.edu',
  },
});

const createCustomPagePresets = (schoolName) => ({
  home: {
    pageTitle: schoolName,
    subtitle: 'Future-ready learning with values, innovation, and student wellbeing at the center.',
    heroImage:
      'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=1800&q=80',
    showHero: true,
    heroHeight: 50,
    heroContentWidth: 100,
    sections: [
      {
        id: 'home-section-1',
        type: 'text',
        title: 'Welcome to Our School',
        body: 'We provide a balanced school experience with strong academics, life skills, sports, and arts for every learner.',
      },
      {
        id: 'home-section-2',
        type: 'image',
        title: 'Modern Campus Experience',
        body: 'Smart classrooms, labs, and collaborative spaces designed for active learning.',
        image:
          'https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=1600&q=80',
      },
      {
        id: 'home-section-3',
        type: 'list',
        title: 'Why Families Choose Us',
        body:
          '1. Structured academics with personal attention\n2. Safe and inclusive campus culture\n3. Co-curricular excellence and leadership opportunities\n4. Strong parent-school communication',
      },
      {
        id: 'home-section-4',
        type: 'cta',
        title: 'Admissions Open',
        body: 'Book a campus visit and connect with our admissions team.',
        buttonLabel: 'Start Admission',
        buttonPath: '/admissions',
      },
    ],
  },
  about: {
    pageTitle: 'About',
    subtitle: `${schoolName} is committed to academic excellence, character building, and meaningful growth.`,
    heroImage:
      'https://images.unsplash.com/photo-1513258496099-48168024aec0?auto=format&fit=crop&w=1800&q=80',
    showHero: true,
    heroHeight: 50,
    heroContentWidth: 100,
    sections: [
      {
        id: 'about-section-1',
        type: 'text',
        title: 'Our Vision',
        body: 'To nurture confident, compassionate, and capable learners prepared for life and leadership.',
      },
      {
        id: 'about-section-2',
        type: 'list',
        title: 'Our Core Pillars',
        body:
          '1. Academic rigor with conceptual clarity\n2. Student wellbeing and mentoring\n3. Innovation, creativity, and critical thinking\n4. Values-driven community engagement',
      },
      {
        id: 'about-section-3',
        type: 'table',
        title: 'School Snapshot',
        tableColumns: ['Metric', 'Value'],
        tableRows: [
          ['Students', '2400+'],
          ['Teachers', '145+'],
          ['Clubs & Activities', '40+'],
          ['Board Results', '98%'],
        ],
      },
      {
        id: 'about-section-4',
        type: 'cta',
        title: 'Explore Our Campus',
        body: 'Schedule a guided campus visit with our team.',
        buttonLabel: 'Contact Us',
        buttonPath: '/contact',
      },
    ],
  },
  academics: {
    pageTitle: 'Academics',
    subtitle: 'Strong foundations, advanced learning pathways, and continuous assessment.',
    heroImage:
      'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&w=1800&q=80',
    showHero: true,
    heroHeight: 50,
    heroContentWidth: 100,
    sections: [
      {
        id: 'academics-section-1',
        type: 'text',
        title: 'Academic Framework',
        body: 'Our curriculum blends core concepts, project-based work, and skill development across all grades.',
      },
      {
        id: 'academics-section-2',
        type: 'table',
        title: 'Program Highlights',
        tableColumns: ['Stage', 'Focus Areas'],
        tableRows: [
          ['Primary', 'Literacy, numeracy, foundational science'],
          ['Middle', 'STEM integration, language fluency, social studies'],
          ['Secondary', 'Board readiness, labs, elective pathways'],
        ],
      },
      {
        id: 'academics-section-3',
        type: 'list',
        title: 'How We Support Learners',
        body:
          '1. Regular formative assessments\n2. Personalized feedback loops\n3. Remedial and enrichment tracks\n4. Mentorship and counseling support',
      },
      {
        id: 'academics-section-4',
        type: 'cta',
        title: 'See Curriculum Details',
        body: 'Connect with our academic team to understand grade-specific pathways.',
        buttonLabel: 'Contact Academic Office',
        buttonPath: '/contact',
      },
    ],
  },
  admissions: {
    pageTitle: 'Admissions',
    subtitle: 'Admission support with counselling, campus visit, application form, and fee guidance.',
    heroImage:
      'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?auto=format&fit=crop&w=1800&q=80',
    showHero: true,
    heroHeight: 50,
    heroContentWidth: 100,
    sections: [
      {
        id: 'admissions-section-1',
        type: 'list',
        title: 'School Admission Process',
        body:
          '1. Connect with our admissions counsellor\n2. Schedule a campus visit and interaction\n3. Complete the application form and submit documents\n4. Fee plan discussion and seat confirmation',
      },
      {
        id: 'admissions-section-2',
        type: 'table',
        title: 'Eligibility by Grade Band',
        tableColumns: ['Grade Band', 'Typical Entry Age', 'Focus'],
        tableRows: [
          ['Nursery to UKG', '3-5 years', 'Foundational readiness and social skills'],
          ['Grade 1 to 5', '6-10 years', 'Core literacy and numeracy progression'],
          ['Grade 6 to 8', '11-13 years', 'Subject depth and project-based learning'],
          ['Grade 9 to 12', '14-17 years', 'Board pathway and career preparation'],
        ],
      },
      {
        id: 'admissions-section-3a',
        type: 'text',
        title: 'Fee, Scholarship and Documents',
        body:
          '<p>Fee plans vary by grade and selected services. Our admissions team shares a complete fee breakup during counselling.</p><p>Common documents include birth certificate, previous report cards, transfer certificate (if applicable), address proof, and passport-size photos.</p><p>Scholarship support may be available through merit-based evaluations and admission assessments.</p>',
      },
      {
        id: 'admissions-section-3',
        type: 'cta',
        title: 'Start Admission Enquiry',
        body: 'Talk to our admissions counsellor to choose the right grade and complete the application quickly.',
        buttonLabel: 'Schedule Counselling',
        buttonPath: '/contact',
      },
    ],
  },
  facilities: {
    pageTitle: 'Facilities',
    subtitle: 'Safe, modern, and student-friendly infrastructure for complete development.',
    heroImage:
      'https://images.unsplash.com/photo-1562774053-701939374585?auto=format&fit=crop&w=1800&q=80',
    showHero: true,
    heroHeight: 50,
    heroContentWidth: 100,
    sections: [
      {
        id: 'facilities-section-1',
        type: 'image',
        title: 'Campus Infrastructure',
        body: 'Purpose-built classrooms, labs, library zones, and sports facilities.',
        image:
          'https://images.unsplash.com/photo-1596495578065-6e0763fa1178?auto=format&fit=crop&w=1600&q=80',
      },
      {
        id: 'facilities-section-2',
        type: 'list',
        title: 'Available Facilities',
        body:
          '1. Science and computer labs\n2. Library and reading corners\n3. Indoor and outdoor sports spaces\n4. Transport and safety monitoring',
      },
      {
        id: 'facilities-section-3',
        type: 'table',
        title: 'Facility Access',
        tableColumns: ['Facility', 'Availability'],
        tableRows: [
          ['Library', 'School hours + activity periods'],
          ['Labs', 'As per class timetable'],
          ['Sports Ground', 'Morning / after-school slots'],
        ],
      },
      {
        id: 'facilities-section-4',
        type: 'cta',
        title: 'Book a Campus Tour',
        body: 'Visit us to experience the learning environment.',
        buttonLabel: 'Plan Visit',
        buttonPath: '/contact',
      },
    ],
  },
  contact: {
    pageTitle: 'Contact',
    subtitle: 'Reach our admissions, academics, and admin teams.',
    heroImage:
      'https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1800&q=80',
    showHero: true,
    heroHeight: 50,
    heroContentWidth: 100,
    sections: [
      {
        id: 'contact-section-1',
        type: 'text',
        title: 'Get in Touch',
        body: 'Our team is available on all working days to support admissions and school-related queries.',
      },
      {
        id: 'contact-section-2',
        type: 'table',
        title: 'Contact Channels',
        tableColumns: ['Department', 'Contact'],
        tableRows: [
          ['Admissions', '+1 (555) 123-4567'],
          ['Email', 'info@yourschool.edu'],
          ['Office Hours', 'Mon-Sat, 8:30 AM - 4:30 PM'],
        ],
      },
      {
        id: 'contact-section-3',
        type: 'cta',
        title: 'Visit Campus',
        body: 'Book an appointment and our team will assist you.',
        buttonLabel: 'Schedule Visit',
        buttonPath: '/admissions',
      },
    ],
  },
  announcements: {
    pageTitle: 'Announcements',
    subtitle: 'Latest school notices, circulars, and important updates.',
    heroImage:
      'https://images.unsplash.com/photo-1460518451285-97b6aa326961?auto=format&fit=crop&w=1800&q=80',
    showHero: true,
    heroHeight: 50,
    heroContentWidth: 100,
    sections: [
      {
        id: 'announcements-section-1',
        type: 'text',
        title: 'Latest Updates',
        body: 'Stay informed about upcoming events, academic notices, and school-wide circulars.',
      },
      {
        id: 'announcements-section-2',
        type: 'table',
        title: 'This Week',
        tableColumns: ['Date', 'Announcement'],
        tableRows: [
          ['Monday', 'PTM schedule published'],
          ['Wednesday', 'Science exhibition registrations close'],
          ['Friday', 'Inter-house sports practice update'],
        ],
      },
      {
        id: 'announcements-section-3',
        type: 'list',
        title: 'Parent Notes',
        body:
          '1. Check student diary updates daily\n2. Ensure ID cards are carried to campus\n3. Contact office for transport timing support',
      },
      {
        id: 'announcements-section-4',
        type: 'cta',
        title: 'Need Clarification?',
        body: 'Reach our help desk for announcement details.',
        buttonLabel: 'Contact Office',
        buttonPath: '/contact',
      },
    ],
  },
  news: {
    pageTitle: 'News',
    subtitle: 'School stories, achievements, and campus updates.',
    heroImage:
      'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?auto=format&fit=crop&w=1800&q=80',
    showHero: true,
    heroHeight: 50,
    heroContentWidth: 100,
    sections: [
      {
        id: 'news-section-1',
        type: 'text',
        title: 'Latest School News',
        body: 'Stay informed with campus achievements, event recaps, and academic milestones.',
      },
      {
        id: 'news-section-2',
        type: 'icon_grid',
        title: 'Top Stories',
        iconCards: [
          {
            icon: '🏆',
            title: 'Inter-School Championship',
            content: 'Our students secured first place in the district science challenge.',
          },
          {
            icon: '🎭',
            title: 'Annual Cultural Fest',
            content: 'Students showcased music, drama, and dance performances.',
          },
          {
            icon: '🧪',
            title: 'STEM Showcase',
            content: 'Middle and senior grades presented innovation and robotics projects.',
          },
        ],
      },
      {
        id: 'news-section-3',
        type: 'social_links',
        title: 'Follow Us for Live Updates',
      },
      {
        id: 'news-section-4',
        type: 'subscribe',
        title: 'Subscribe for News Alerts',
        body: 'Get school news, event highlights, and important updates by email.',
      },
    ],
  },
  careers: {
    templateVersion: 'careers-v3',
    pageTitle: 'Job Offers',
    subtitle: 'Simple list of current openings.',
    heroImage:
      'https://images.unsplash.com/photo-1577896851231-70ef18881754?auto=format&fit=crop&w=1800&q=80',
    showHero: true,
    heroHeight: 22,
    heroContentWidth: 90,
    sections: [
      {
        id: 'careers-section-1',
        type: 'careers_openings',
        title: 'Current Job Offers',
        body: 'Browse the available roles and click Apply for details.',
        careersLayout: 'vertical',
        careersColumns: 1,
        careerCards: [
          {
            title: 'Primary Teacher',
            department: 'Academics',
            location: 'Main Campus',
            employmentType: 'Full Time',
            applyPath: '/contact',
            image:
              'https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1200&q=80',
            summary: 'Teach foundational grades with activity-based learning methods.',
          },
          {
            title: 'Mathematics Teacher',
            department: 'Academics',
            location: 'Main Campus',
            employmentType: 'Full Time',
            applyPath: '/contact',
            image:
              'https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=1200&q=80',
            summary: 'Handle middle and senior grade mathematics with assessment planning.',
          },
          {
            title: 'School Counselor',
            department: 'Student Support',
            location: 'Main Campus',
            employmentType: 'Full Time',
            applyPath: '/contact',
            image:
              'https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1200&q=80',
            summary: 'Provide student wellbeing support and parent coordination.',
          },
        ],
      },
    ],
  },
  results: {
    templateVersion: 'results-v4',
    pageTitle: 'Results & Academic Achievements',
    subtitle:
      'The school publishes verified academic achievements and examination results for parents, students, and visitors.',
    heroImage:
      'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&w=2200&q=80',
    showHero: true,
    heroHeight: 38,
    heroContentWidth: 86,
    sections: [
      {
        id: 'results-section-0',
        type: 'divider',
        dividerLabel: 'School Results',
        dividerColor: '#0B3A67',
        dividerThickness: 2,
        sectionBgColor: '#F3FAFF',
        sectionBorderEnabled: false,
      },
      {
        id: 'results-section-1',
        type: 'text',
        title: 'Academic Performance Overview',
        body:
          '<p>Our school consistently shares board outcomes and academic milestones through a transparent and structured reporting process.</p><p>Each result cycle includes topper details, class-wise performance highlights, and clear performance indicators for students and families.</p>',
        sectionBgColor: '#FFFFFF',
        sectionBorderEnabled: false,
        sectionTitleColor: '#0B3A67',
        sectionTitleAlign: 'center',
      },
      {
        id: 'results-section-2',
        type: 'tabs',
        title: 'Board Results',
        body: 'Class-wise board outcomes, toppers, and key academic highlights.',
        sectionBgColor: '#FFFFFF',
        sectionBorderEnabled: false,
        sectionTitleColor: '#0B3A67',
        tabs: [
          {
            title: 'Grade 10',
            content:
              '<p><strong>Topper:</strong> Ananya Sharma - 97.8%</p><p><strong>Pass Percentage:</strong> 98.4%</p><p><strong>Highlights:</strong></p><ul><li>42 students scored above 90%</li><li>100% pass rate in Mathematics and Science</li></ul>',
          },
          {
            title: 'Grade 12',
            content:
              '<p><strong>Topper:</strong> Rahul Verma - 96.6%</p><p><strong>Pass Percentage:</strong> 96.9%</p><p><strong>Highlights:</strong></p><ul><li>31 students scored above 90%</li><li>Strong performance in Commerce and Computer Science streams</li></ul>',
          },
        ],
      },
      {
        id: 'results-section-3',
        type: 'text',
        title: 'Student Result Portal',
        body:
          '<p>Students can check their results securely through the online portal.</p><p><strong>Required Details:</strong></p><ul><li>Roll Number</li><li>Date of Birth (DD/MM/YYYY)</li></ul><p><strong>Portal Features:</strong></p><ul><li>Instant result lookup</li><li>Subject-wise marks view</li><li>Downloadable marksheet (PDF)</li></ul>',
        sectionBgColor: '#FFFFFF',
        sectionBorderEnabled: false,
        sectionTitleColor: '#0B3A67',
      },
      {
        id: 'results-section-4',
        type: 'icon_grid',
        title: 'Result Statistics',
        body: 'Overall performance indicators for the latest academic cycle.',
        sectionBgColor: '#EAF5FF',
        sectionBorderEnabled: false,
        sectionTitleColor: '#0B3A67',
        sectionTitleAlign: 'center',
        iconGridColumns: 4,
        iconCards: [
          { icon: '🎯', title: 'Overall Pass Percentage', content: '97.6% across all board cohorts.' },
          { icon: '🏅', title: 'Distinctions', content: '68% of students scored 75% and above.' },
          { icon: '📘', title: 'First Class', content: '24% of students scored 60% and above.' },
          {
            icon: '📊',
            title: 'Subject-Wise Achievement',
            content: 'Mathematics 95%, Science 97%, English 99% pass performance.',
          },
        ],
      },
      {
        id: 'results-section-5',
        type: 'table',
        title: 'Subject-Wise Achievements',
        sectionBgColor: '#F7FBFF',
        sectionBorderEnabled: false,
        sectionTitleColor: '#0B3A67',
        tableColumns: ['Subject', 'Pass Percentage', 'Students Above 90%', 'Highlights'],
        tableRows: [
          ['Mathematics', '95%', '28', 'Excellent conceptual performance in Grade 10 and Grade 12.'],
          ['Science', '97%', '34', 'Strong practical and theory outcomes across both board classes.'],
          ['English', '99%', '39', 'Consistent high scores in language and writing sections.'],
          ['Social Science', '96%', '24', 'Improved analytical and case-based answer quality.'],
          ['Computer Science', '98%', '26', 'Outstanding performance in application-based questions.'],
        ],
      },
      {
        id: 'results-section-6',
        type: 'student_results_grid',
        title: 'Top Performers',
        body: 'Students recognized for outstanding marks and achievements.',
        resultsMetricMode: 'both',
        resultsGridColumns: 3,
        sectionBgColor: '#FFFFFF',
        sectionBorderEnabled: false,
        sectionTitleColor: '#0B3A67',
        sectionTitleAlign: 'center',
        resultCards: [
          {
            name: 'Ananya Sharma',
            className: 'Grade 10',
            percentage: '97.8%',
            marks: '489/500',
            image:
              'https://images.unsplash.com/photo-1545239351-1141bd82e8a6?auto=format&fit=crop&w=500&q=80',
            highlight: 'School Topper',
          },
          {
            name: 'Rahul Verma',
            className: 'Grade 12',
            percentage: '96.6%',
            marks: '483/500',
            image:
              'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=500&q=80',
            highlight: 'School Topper',
          },
          {
            name: 'Meera Iyer',
            className: 'Grade 12',
            percentage: '95.9%',
            marks: '480/500',
            image:
              'https://images.unsplash.com/photo-1547425260-76bcadfb4f2c?auto=format&fit=crop&w=500&q=80',
            highlight: 'Excellence in Science',
          },
          {
            name: 'Arjun Nair',
            className: 'Grade 10',
            percentage: '95.4%',
            marks: '477/500',
            image:
              'https://images.unsplash.com/photo-1507591064344-4c6ce005b128?auto=format&fit=crop&w=500&q=80',
            highlight: 'Excellence in Mathematics',
          },
          {
            name: 'Sana Khan',
            className: 'Grade 12',
            percentage: '95.1%',
            marks: '476/500',
            image:
              'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?auto=format&fit=crop&w=500&q=80',
            highlight: 'Excellence in Commerce',
          },
        ],
      },
      {
        id: 'results-section-7',
        type: 'cta',
        title: 'Student Result Support',
        body: 'For transcript requests, re-evaluation, or result clarification, contact the school result desk.',
        buttonLabel: 'Contact Result Desk',
        buttonPath: '/contact',
        buttonEnabled: true,
        buttonVariant: 'contained',
        buttonBgColor: '#0B3A67',
        buttonTextColor: '#FFFFFF',
        sectionBgColor: '#FFFFFF',
        sectionBorderEnabled: false,
        sectionTitleColor: '#0B3A67',
        sectionTitleAlign: 'center',
        sectionTitleFontSize: 28,
      },
    ],
  },
});

const createSupplementalCustomSections = (slug) => {
  switch (slug) {
    case 'home':
      return [
        {
          id: 'home-section-extra-0',
          type: 'stages',
          title: 'School Stages',
          body:
            '1. Early Years: Play-based confidence and communication\n2. Primary Years: Core literacy and numeracy mastery\n3. Middle School: Inquiry, projects and experiments\n4. Senior School: Board success and career readiness',
        },
        {
          id: 'home-section-extra-1',
          type: 'table',
          title: 'Campus At A Glance',
          tableColumns: ['Area', 'Highlights'],
          tableRows: [
            ['Academics', 'STEM labs, language programs, mentoring'],
            ['Activities', 'Clubs, arts, sports, leadership programs'],
            ['Support', 'Counselling, remedial and enrichment support'],
          ],
          animationStyle: 'zoom-in',
        },
        {
          id: 'home-section-extra-2',
          type: 'quote',
          title: 'Parent Voice',
          body: '"A positive learning environment with consistent academic support and excellent communication."',
        },
      ];
    case 'about':
      return [
        {
          id: 'about-section-extra-1',
          type: 'image',
          title: 'Leadership Note',
          body: 'Our leadership team works closely with teachers and families to create a values-driven school culture.',
          image:
            'https://images.unsplash.com/photo-1544717301-9cdcb1f5940f?auto=format&fit=crop&w=1600&q=80',
          imageFrameShape: 'rectangle',
        },
        {
          id: 'about-section-extra-2',
          type: 'cta',
          title: 'Meet Our Team',
          body: 'Book a guided school interaction session with leadership and coordinators.',
          buttonLabel: 'Schedule Interaction',
          buttonPath: '/contact',
        },
      ];
    case 'academics':
      return [
        {
          id: 'academics-section-extra-0',
          type: 'results',
          title: 'Recent Board Results',
          body: 'Our students consistently secure excellent outcomes across core and elective subjects.',
        },
        {
          id: 'academics-section-extra-1',
          type: 'dropdown',
          title: 'Explore Curriculum Stage',
          body: 'Select a curriculum stage to start a focused discussion with our academic office.',
          dropdownOptions: ['primary', 'middle', 'secondary'],
          dropdownPlaceholder: 'Choose curriculum stage',
          buttonLabel: 'Open Contact',
          buttonPath: '/contact',
        },
        {
          id: 'academics-section-extra-2',
          type: 'quote',
          title: 'Academic Promise',
          body: '"Every child receives structured guidance, assessment feedback, and growth opportunities."',
        },
      ];
    case 'admissions':
      return [
        {
          id: 'admissions-section-extra-1',
          type: 'dropdown',
          title: 'Select Grade Band for Enquiry',
          body: 'Choose your preferred grade band and continue with admissions support.',
          dropdownOptions: ['Nursery to UKG', 'Grade 1 to 5', 'Grade 6 to 8', 'Grade 9 to 12'],
          dropdownPlaceholder: 'Select grade band',
          buttonLabel: 'Start Enquiry',
          buttonPath: '/contact',
        },
        {
          id: 'admissions-section-extra-2',
          type: 'table',
          title: 'Fee Structure Overview',
          tableColumns: ['Fee Head', 'How It Works'],
          tableRows: [
            ['Registration Fee', 'One-time during application'],
            ['Admission Fee', 'Payable at enrolment confirmation'],
            ['Tuition Fee', 'Quarterly / annual plan by grade'],
            ['Optional Services', 'Transport, meals, activities (as selected)'],
          ],
        },
        {
          id: 'admissions-section-extra-3',
          type: 'image',
          title: 'Admissions Counselling Desk',
          body: 'Our counsellors guide parents through eligibility, documentation, and school orientation.',
          image:
            'https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1600&q=80',
        },
      ];
    case 'facilities':
      return [
        {
          id: 'facilities-section-extra-1',
          type: 'table',
          title: 'Facility Matrix',
          tableColumns: ['Facility', 'Purpose', 'Access'],
          tableRows: [
            ['STEM Lab', 'Practical science and coding', 'Timetable slots'],
            ['Library', 'Reading and research', 'Daily + supervised hours'],
            ['Sports Block', 'Fitness and team sports', 'Morning and activity periods'],
          ],
        },
        {
          id: 'facilities-section-extra-2',
          type: 'quote',
          title: 'Student Feedback',
          body: '"The labs and sports areas are well maintained and easy to access during school activities."',
        },
      ];
    case 'contact':
      return [
        {
          id: 'contact-section-extra-1',
          type: 'dropdown',
          title: 'Choose Support Team',
          body: 'Select a department to quickly connect with the right contact point.',
          dropdownOptions: ['Admissions', 'Academics', 'Transport', 'Accounts'],
          dropdownPlaceholder: 'Select department',
          buttonLabel: 'Open Contact',
          buttonPath: '/contact',
        },
        {
          id: 'contact-section-extra-2',
          type: 'cta',
          title: 'Plan Your Visit',
          body: 'Campus visits are available on working days by prior appointment.',
          buttonLabel: 'Book Visit',
          buttonPath: '/admissions',
        },
      ];
    case 'announcements':
      return [
        {
          id: 'announcements-section-extra-1',
          type: 'image',
          title: 'Event Highlights',
          body: 'Recent updates from events, assemblies, and house activities.',
          image:
            'https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=1600&q=80',
        },
        {
          id: 'announcements-section-extra-2',
          type: 'button',
          title: 'Quick Contact',
          buttonLabel: 'Ask Helpdesk',
          buttonPath: '/contact',
          buttonVariant: 'outlined',
          buttonBgColor: '#8B1E2B',
          buttonTextColor: '#8B1E2B',
        },
      ];
    case 'news':
      return [
        {
          id: 'news-section-extra-1',
          type: 'image_grid',
          title: 'Campus Moments',
          body: 'Snapshots from school events, competitions, and classroom activities.',
        },
      ];
    case 'careers':
      return [];
    default:
      return [];
  }
};

const createCustomPagesIndex = () => ({
  pages: [
    { slug: 'home', title: 'Home', menuLabel: 'Home', path: '/', showInMenu: true, pageSlug: 'home_page' },
    { slug: 'about', title: 'About', menuLabel: 'About', path: '/about', showInMenu: true, pageSlug: 'about_page' },
    {
      slug: 'academics',
      title: 'Academics',
      menuLabel: 'Academics',
      path: '/academics',
      showInMenu: true,
      pageSlug: 'academics_page',
    },
    {
      slug: 'admissions',
      title: 'Admissions',
      menuLabel: 'Admissions',
      path: '/admissions',
      showInMenu: true,
      pageSlug: 'enrollment_options',
    },
    {
      slug: 'facilities',
      title: 'Facilities',
      menuLabel: 'Facilities',
      path: '/facilities',
      showInMenu: true,
      pageSlug: 'facilities_page',
    },
    {
      slug: 'results',
      title: 'Results',
      menuLabel: 'Results',
      path: '/results',
      showInMenu: true,
      pageSlug: 'custom_results',
    },
    {
      slug: 'announcements',
      title: 'Announcements',
      menuLabel: 'Announcements',
      path: '/announcements',
      showInMenu: true,
      pageSlug: 'custom_announcements',
    },
    { slug: 'news', title: 'News', menuLabel: 'News', path: '/page/news', showInMenu: true, pageSlug: 'custom_news' },
    { slug: 'careers', title: 'Careers', menuLabel: 'Careers', path: '/careers', showInMenu: true, pageSlug: 'custom_careers' },
    { slug: 'contact', title: 'Contact', menuLabel: 'Contact', path: '/contact', showInMenu: true, pageSlug: 'contact_us_page' },
  ],
});

const createDefaultPages = (schoolNameValue) => {
  const schoolName = normalizeSchoolName(schoolNameValue);
  const customPagePresets = createCustomPagePresets(schoolName);
  const customPages = Object.entries(customPagePresets)
    .filter(([slug]) => !isCoreWebsiteRouteSlug(slug) && DEFAULT_CUSTOM_PAGE_ROUTE_SLUGS.includes(slug))
    .map(([slug, content]) => ({
      pageSlug: toCustomPageSlug(slug),
      componentKey: 'customPage',
      content: {
        ...content,
        heroHeight: 25,
        heroContentWidth: 100,
        sectionSpacing: 0,
        sections: [...(content.sections || []), ...createSupplementalCustomSections(slug)].map(
          (section, index) => ({
            ...section,
            animationStyle: section.animationStyle || 'fade-up',
            animationDuration: section.animationDuration ?? 700,
            animationDelay: section.animationDelay ?? index * 90,
          })
        ),
      },
    }));

  return [
    {
      pageSlug: 'header_page',
      componentKey: 'header',
      content: createHeaderContent(schoolName),
    },
    {
      pageSlug: 'home_page',
      componentKey: 'home',
      content: createHomeContent(schoolName),
    },
    {
      pageSlug: 'sidebar',
      componentKey: 'sidebar',
      content: createSidebarContent(schoolName),
    },
    {
      pageSlug: 'footer',
      componentKey: 'footer',
      content: createFooterContent(schoolName),
    },
    {
      pageSlug: 'about_page',
      componentKey: 'about_page',
      content: createAboutContent(schoolName),
    },
    {
      pageSlug: 'contact_us_page',
      componentKey: 'contact_us_page',
      content: createContactContent(schoolName),
    },
    {
      pageSlug: 'facilities_page',
      componentKey: 'facilities_page',
      content: createFacilitiesContent(),
    },
    {
      pageSlug: 'academics_page',
      componentKey: 'academics_page',
      content: createAcademicsContent(),
    },
    {
      pageSlug: 'enrollment_options',
      componentKey: 'enrollment_options',
      content: createEnrollmentContent(),
    },
    ...customPages,
    {
      pageSlug: 'custom_pages_index',
      componentKey: 'customPagesIndex',
      content: createCustomPagesIndex(),
    },
  ];
};

const fetchExistingPage = async (tenantKey, pageSlug) => {
  try {
    const response = await axiosInstance.get(`/api/${tenantKey}/pages/${pageSlug}`);
    return response.data;
  } catch (error) {
    if (error?.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

const seedPage = async ({ schoolId, pageSlug, componentKey, content }) => {
  const payload = {
    pageSlug,
    componentKey,
    content: JSON.stringify(content),
    schoolId,
  };

  await axiosInstance.post(`/api/${schoolId}/pages/${pageSlug}`, payload, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const initializeDefaultWebsiteData = async ({
  schoolId,
  tenantKey,
  schoolName,
} = {}) => {
  if (!schoolId) {
    throw new Error('Missing schoolId for default website initialization.');
  }

  if (!tenantKey) {
    throw new Error('Missing tenantKey for default website initialization.');
  }

  const pages = createDefaultPages(schoolName);
  const created = [];
  const skipped = [];
  const failed = [];

  for (const page of pages) {
    try {
      const existing = await fetchExistingPage(tenantKey, page.pageSlug);
      const shouldSeed = !existing || !hasUsableContent(existing);

      if (!shouldSeed) {
        skipped.push(page.pageSlug);
        continue;
      }

      await seedPage({
        schoolId,
        pageSlug: page.pageSlug,
        componentKey: page.componentKey,
        content: page.content,
      });
      created.push(page.pageSlug);
    } catch (error) {
      failed.push({
        pageSlug: page.pageSlug,
        message:
          error?.response?.data?.message ||
          error?.response?.data ||
          error?.message ||
          'Failed to initialize page',
      });
    }
  }

  return {
    created,
    skipped,
    failed,
  };
};

export const getDefaultWebsitePages = (schoolName) => createDefaultPages(schoolName);
